import React, { useEffect } from "react";
import { Toaster, toast } from "sonner";
import "./ToastNotifications.css";

let lastToastKey = "";
let lastToastAt = 0;
const DEDUPE_WINDOW_MS = 1600;

const defaults = {
  success: 4500,
  error: 6500,
  warning: 5500,
  info: 4500
};

const publish = (type, message, options = {}) => {
  const text = typeof message === "string" ? message.trim() : "";
  if (!text) return null;

  const now = Date.now();
  const key = `${type}:${text}`;
  if (key === lastToastKey && now - lastToastAt < DEDUPE_WINDOW_MS) return null;
  lastToastKey = key;
  lastToastAt = now;

  const duration = Number.isFinite(options.duration) ? Math.max(0, options.duration) : defaults[type];
  return toast.custom((id) => (
    <ToastItem
      type={type}
      message={text}
      duration={duration}
      onDismiss={() => toast.dismiss(id)}
    />
  ), { duration: duration || Infinity });
};

export const showSuccess = (message, options) => publish("success", message, options);
export const showError = (message, options) => publish("error", message, options);
export const showWarning = (message, options) => publish("warning", message, options);
export const showInfo = (message, options) => publish("info", message, options);

export const ToastFeedback = ({ message, type = "info" }) => {
  useEffect(() => {
    if (message) publish(type, message);
  }, [message, type]);
  return null;
};

const icons = {
  success: <path d="m5 12 4 4L19 6" />,
  error: <path d="M12 8v4m0 4h.01M10.3 3.9 2.8 17a2 2 0 0 0 1.7 3h15a2 2 0 0 0 1.7-3l-7.5-13.1a2 2 0 0 0-3.4 0Z" />,
  warning: <path d="M12 9v4m0 4h.01M10.3 3.9 2.8 17a2 2 0 0 0 1.7 3h15a2 2 0 0 0 1.7-3l-7.5-13.1a2 2 0 0 0-3.4 0Z" />,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v5m0-8h.01" /></>
};

const ToastItem = ({ type, message, duration, onDismiss }) => {
  return (
    <div className={`app-toast app-toast--${type}`} role={type === "error" ? "alert" : "status"} aria-live={type === "error" ? "assertive" : "polite"}>
      <span className="app-toast__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{icons[type]}</svg>
      </span>
      <p className="app-toast__message">{message}</p>
      <button className="app-toast__close" type="button" onClick={onDismiss} aria-label="Dismiss notification">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
      </button>
      {duration > 0 && <span className="app-toast__progress" style={{ animationDuration: `${duration}ms` }} aria-hidden="true" />}
    </div>
  );
};

const ToastNotifications = ({ children }) => {
  return (
    <>
      {children}
      <Toaster position="top-right" visibleToasts={5} gap={10} duration={4500} closeButton={false} containerAriaLabel="Notifications" className="app-toast-region" offset={20} mobileOffset={12} style={{ width: "min(400px, calc(100vw - 32px))", zIndex: 2000 }} />
    </>
  );
};

export default ToastNotifications;
